import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle, Camera, Sparkles, Sun, Focus } from 'lucide-react';
import { useLanguage } from '../../context/AppContext';

/**
 * Non-blocking photo-quality warning.
 * Shown BEFORE sending the image to the Vision AI when we detect the photo
 * is too dark or blurry. The user keeps final say via "Analyser quand même".
 */
const LowQualityPhotoModal = ({
  open,
  onOpenChange,
  quality, // { isDark, isBlurry, brightness, blurScore }
  language = 'fr',
  onAnalyzeAnyway,
  onRetake,
}) => {
  const { t } = useLanguage();
  const fr = language === 'fr';
  const reasons = [];
  if (quality?.isDark) {
    reasons.push({
      icon: <Sun className="w-4 h-4 text-amber-400" />,
      label: fr ? "Éclairage faible" : 'Low light',
      detail: fr
        ? "La photo est un peu sombre — le coach pourrait mal identifier certains aliments."
        : 'The photo looks dim — the coach may misread some foods.',
    });
  }
  if (quality?.isBlurry) {
    reasons.push({
      icon: <Focus className="w-4 h-4 text-sky-400" />,
      label: fr ? 'Netteté limitée' : 'Blurry',
      detail: fr
        ? "L'image semble floue. Stabilise ton téléphone ou rapproche-toi."
        : 'The image looks blurry. Steady your phone or move closer.',
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-card border-amber-500/40 max-w-md"
        data-testid="low-quality-photo-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            {t('modals.low_photo_title')}
          </DialogTitle>
          <DialogDescription>
            {fr
              ? "Petit coup de pouce avant d'envoyer à la Vision AI."
              : 'Quick heads-up before sending to the Vision AI.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {reasons.map((r, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20"
              data-testid={`low-quality-reason-${i}`}
            >
              <div className="mt-0.5 shrink-0">{r.icon}</div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-foreground">{r.label}</p>
                <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{r.detail}</p>
              </div>
            </div>
          ))}

          <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              {fr
                ? "Astuce : photo prise d'en haut, bon éclairage, toute l'assiette visible — taux de succès maximal."
                : 'Tip: top-down shot, good light, full plate visible — highest accuracy.'}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button
            variant="outline"
            className="flex-1 border-border"
            onClick={onRetake}
            data-testid="low-quality-retake-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            {t('modals.low_photo_retry')}
          </Button>
          <Button
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={onAnalyzeAnyway}
            data-testid="low-quality-analyze-anyway-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {t('modals.low_photo_continue')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LowQualityPhotoModal;
